import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Building,
  Mail,
  Phone,
  MapPin,
  FileText,
  Users,
  User,
  Globe,
  Hash,
  Save,
  X
} from 'lucide-react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';

interface Client {
  id: string;
  nom: string;
  email: string;
  telephone: string;
  adresse: string;
  code_postal: string;
  ville: string;
  pays: string;
  type_client: 'particulier' | 'entreprise' | 'avocat' | 'assurance';
  siret?: string;
  siren?: string;
  notes: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Interface pour l'API SIRET (pas utilisée actuellement)
/*
interface SiretApiResponse {
  unite_legale: {
    denomination: string;
    sigle?: string;
    activite_principale: string;
    adresse: {
      complement_adresse?: string;
      numero_voie?: string;
      type_voie?: string;
      libelle_voie?: string;
      code_postal: string;
      libelle_commune: string;
    };
  };
  etablissement: {
    adresse: {
      complement_adresse?: string;
      numero_voie?: string;
      type_voie?: string;
      libelle_voie?: string;
      code_postal: string;
      libelle_commune: string;
    };
  };
}
*/

const ClientManager: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'particulier' | 'entreprise' | 'avocat' | 'assurance'>('all');
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [siretLoading, setSiretLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'nom' | 'email' | 'ville' | 'type_client'>('nom');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    type_client: 'particulier' as 'particulier' | 'entreprise' | 'avocat' | 'assurance',
    siret: '',
    siren: '',
    notes: ''
  });

  // Charger les clients depuis l'API
  useEffect(() => {
    loadClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.error('Aucun token d\'authentification trouvé');
        setIsLoading(false);
        // Rediriger vers la page de connexion
        navigate('/login');
        return;
      }
      
      console.log('Token utilisé:', token.substring(0, 50) + '...');
      
      const response = await fetch(API_ENDPOINTS.CLIENTS, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.data || []);
      } else {
        const error = await response.json();
        console.error('Erreur API clients:', error);
        
        // Si le token est invalide, rediriger vers la connexion
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Recherche automatique via SIRET/SIREN
  const searchBySiret = async (siret: string) => {
    if (!siret || siret.length < 14) return;
    
    try {
      setSiretLoading(true);
      // Utiliser l'API backend pour la recherche SIRET
      const response = await fetch(API_ENDPOINTS.SIRET_LOOKUP(siret), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const data = result.data;
          
          // Remplir automatiquement les champs
          setFormData(prev => ({
            ...prev,
            nom: data.denomination || '',
            adresse: data.adresse || '',
            code_postal: data.code_postal || '',
            ville: data.ville || '',
            type_client: 'entreprise',
            siren: data.siren || siret.substring(0, 9)
          }));
          
          console.log('Données SIRET récupérées:', data);
        } else {
          console.error('Aucune donnée trouvée pour ce SIRET');
        }
      } else {
        console.error('Erreur API SIRET:', response.status);
        // Fallback - garder au moins le SIRET et passer en mode entreprise
        setFormData(prev => ({
          ...prev,
          type_client: 'entreprise',
          siren: siret.substring(0, 9)
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la recherche SIRET:', error);
      // Fallback - garder au moins le SIRET et passer en mode entreprise
      setFormData(prev => ({
        ...prev,
        type_client: 'entreprise',
        siren: siret.substring(0, 9)
      }));
    } finally {
      setSiretLoading(false);
    }
  };

  // Gérer le changement de SIRET
  const handleSiretChange = (value: string) => {
    setFormData(prev => ({ ...prev, siret: value }));
    
    // Recherche automatique si SIRET complet
    if (value.length === 14) {
      searchBySiret(value);
    }
  };

  // Sauvegarder un client
  const saveClient = async () => {
    try {
      // Validation email obligatoire
      if (!formData.email || !formData.email.trim()) {
        alert('L\'email est requis');
        return;
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        alert('Format d\'email invalide');
        return;
      }
      
      // Validation spécifique selon le type de client
      if (formData.type_client === 'entreprise') {
        // Pour les entreprises : nom OU SIRET obligatoire
        if (!formData.nom.trim() && !formData.siret.trim()) {
          alert('Pour une entreprise, le nom ou le SIRET est requis (l\'un ou l\'autre)');
          return;
        }
        
        // Si SIRET fourni, valider le format
        if (formData.siret && formData.siret.length !== 14) {
          alert('Le SIRET doit contenir exactement 14 chiffres');
          return;
        }
      } else {
        // Pour les particuliers, avocats, assurances : nom obligatoire
        if (!formData.nom.trim()) {
          alert('Le nom du client est requis');
          return;
        }
      }
      
      const method = editingClient ? 'PUT' : 'POST';
      const url = editingClient ? `/api/clients/${editingClient.id}` : '/api/clients';
      
      // Transformer les données pour correspondre au backend
      const clientData = {
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        adresse: formData.adresse,
        codePostal: formData.code_postal,
        ville: formData.ville,
        pays: formData.pays,
        typeClient: formData.type_client,
        siret: formData.siret,
        siren: formData.siren,
        notes: formData.notes
      };
      
      console.log('Sauvegarde client:', { method, url, clientData });
      
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await fetch(buildApiUrl(url.replace('/api', '')), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clientData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Client sauvegardé avec succès:', result);
        await loadClients();
        resetForm();
      } else {
        const error = await response.json();
        console.error('Erreur API:', error);
        
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        } else {
          alert('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Supprimer un client
  const deleteClient = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    
    try {
      const response = await fetch(buildApiUrl(`/clients/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        await loadClients();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      adresse: '',
      code_postal: '',
      ville: '',
      pays: 'France',
      type_client: 'particulier',
      siret: '',
      siren: '',
      notes: ''
    });
    setIsAddingClient(false);
    setEditingClient(null);
  };

  // Ouvrir l'édition d'un client
  const openEditClient = (client: Client) => {
    console.log('Ouverture édition client:', client);
    setFormData({
      nom: client.nom || '',
      email: client.email || '',
      telephone: client.telephone || '',
      adresse: client.adresse || '',
      code_postal: client.code_postal || '',
      ville: client.ville || '',
      pays: client.pays || 'France',
      type_client: client.type_client || 'particulier',
      siret: client.siret || '',
      siren: client.siren || '',
      notes: client.notes || ''
    });
    setEditingClient(client);
    setIsAddingClient(true);
  };

  // Filtrer et trier les clients
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.ville.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || client.type_client === filterType;
    
    return matchesSearch && matchesFilter;
  }).sort((a, b) => {
    const aValue = a[sortBy]?.toLowerCase() || '';
    const bValue = b[sortBy]?.toLowerCase() || '';
    
    if (sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Fonction pour changer le tri
  const handleSort = (column: 'nom' | 'email' | 'ville' | 'type_client') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'entreprise': return <Building className="w-4 h-4" />;
      case 'avocat': return <FileText className="w-4 h-4" />;
      case 'assurance': return <Building className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'entreprise': return 'bg-blue-500/20 text-blue-400';
      case 'avocat': return 'bg-purple-500/20 text-purple-400';
      case 'assurance': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Gestion des Clients</h2>
        <button
          onClick={() => setIsAddingClient(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-2xl text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          <span>Nouveau Client</span>
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl p-6 rounded-3xl border border-gray-700/50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-400 whitespace-nowrap">Type :</span>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-48 px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
            >
              <option value="all">🔍 Tous les types</option>
              <option value="particulier">👤 Particulier</option>
              <option value="entreprise">🏢 Entreprise</option>
              <option value="avocat">⚖️ Avocat</option>
              <option value="assurance">🛡️ Assurance</option>
            </select>
          </div>
        </div>
        
        {/* Statistiques */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/30">
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <span>Total : {clients.length} clients</span>
            <span>•</span>
            <span>Affichés : {filteredClients.length}</span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Entreprises : {clients.filter(c => c.type_client === 'entreprise').length}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <span>Particuliers : {clients.filter(c => c.type_client === 'particulier').length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-700/50">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Chargement des clients...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">Aucun client trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('nom')}
                      className="flex items-center space-x-1 hover:text-orange-400 transition-colors"
                    >
                      <span>Client</span>
                      {sortBy === 'nom' && (
                        <span className="text-orange-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('type_client')}
                      className="flex items-center space-x-1 hover:text-orange-400 transition-colors"
                    >
                      <span>Type</span>
                      {sortBy === 'type_client' && (
                        <span className="text-orange-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('email')}
                      className="flex items-center space-x-1 hover:text-orange-400 transition-colors"
                    >
                      <span>Contact</span>
                      {sortBy === 'email' && (
                        <span className="text-orange-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('ville')}
                      className="flex items-center space-x-1 hover:text-orange-400 transition-colors"
                    >
                      <span>Localisation</span>
                      {sortBy === 'ville' && (
                        <span className="text-orange-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredClients.map((client) => (
                  <tr 
                    key={client.id} 
                    className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                    onClick={() => openEditClient(client)}
                    title="Cliquer pour modifier ce client"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                          {getTypeIcon(client.type_client)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {client.nom}
                          </div>
                          {client.siret && (
                            <div className="text-sm text-gray-400">
                              SIRET: {client.siret}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(client.type_client)}`}>
                        {getTypeIcon(client.type_client)}
                        <span className="ml-1 capitalize">{client.type_client}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {client.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {client.email}
                          </div>
                        )}
                        {client.telephone && (
                          <div className="flex items-center mt-1">
                            <Phone className="w-4 h-4 mr-1" />
                            {client.telephone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {client.ville && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {client.ville}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditClient(client);
                          }}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                          title="Modifier ce client"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Modifier</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteClient(client.id);
                          }}
                          className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                          title="Supprimer ce client"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal d'ajout/édition */}
      {isAddingClient && (
        <div 
          className="fixed inset-0 w-screen h-screen flex items-center justify-center z-[99999] p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={resetForm}
        >
          <div 
            className="w-full max-w-4xl bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700/50 transform transition-all duration-300 ease-out animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header moderne */}
            <div className="relative p-8 bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-b border-gray-700/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-1">
                      {editingClient ? 'Modifier le client' : 'Nouveau client'}
                    </h3>
                    <p className="text-gray-400">
                      {editingClient ? 'Mettre à jour les informations' : 'Ajouter un nouveau client au système'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetForm}
                  className="w-12 h-12 rounded-2xl bg-gray-700/50 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8 overflow-y-auto flex-1">
              {/* Type de client */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-300 mb-4">Type de client *</label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: 'particulier', label: 'Particulier' },
                    { value: 'entreprise', label: 'Entreprise' },
                    { value: 'avocat', label: 'Avocat' },
                    { value: 'assurance', label: 'Assurance' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type_client: type.value as any }))}
                      className={`px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                        formData.type_client === type.value
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                          : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* SIRET (pour entreprises) */}
                {formData.type_client === 'entreprise' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      SIRET (optionnel si nom fourni)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.siret}
                        onChange={(e) => handleSiretChange(e.target.value)}
                        className="w-full px-4 py-4 pr-12 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                        placeholder="14 chiffres - remplit automatiquement les données"
                        maxLength={14}
                        pattern="[0-9]{14}"
                      />
                      {siretLoading && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      <span className="text-orange-400">⚡</span> Le SIRET permet de récupérer automatiquement les informations via l'API INSEE
                    </p>
                  </div>
                )}

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {formData.type_client === 'entreprise' ? 
                      'Nom (optionnel si SIRET fourni)' : 
                      'Nom *'
                    }
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                    className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder={formData.type_client === 'entreprise' ? 'Nom de l\'entreprise' : 'Nom du client'}
                    required={formData.type_client !== 'entreprise'}
                  />
                  {formData.type_client === 'entreprise' && (
                    <p className="text-xs text-gray-400 mt-1">
                      Le nom est optionnel si vous fournissez un SIRET valide
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${!formData.email ? 'border-red-500/50 focus:border-red-500' : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'} focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                    placeholder="email@exemple.com"
                    required
                  />
                  {!formData.email && (
                    <p className="text-xs text-red-400 mt-1">
                      📧 L'email est requis pour tous les clients
                    </p>
                  )}
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                    className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                {/* Ville */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData(prev => ({ ...prev, ville: e.target.value }))}
                    className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="Paris"
                  />
                </div>

                {/* Code postal */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={formData.code_postal}
                    onChange={(e) => setFormData(prev => ({ ...prev, code_postal: e.target.value }))}
                    className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="75001"
                    maxLength={5}
                  />
                </div>

                {/* Pays */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Pays
                  </label>
                  <input
                    type="text"
                    value={formData.pays}
                    onChange={(e) => setFormData(prev => ({ ...prev, pays: e.target.value }))}
                    className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="France"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData(prev => ({ ...prev, adresse: e.target.value }))}
                  className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                  placeholder="123 Rue de la Paix"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full h-24 px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300 resize-none"
                  placeholder="Notes sur le client..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 p-8 border-t border-gray-700/30">
              <button
                onClick={resetForm}
                className="px-8 py-4 bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white rounded-2xl font-medium transition-all duration-300 backdrop-blur-sm"
              >
                Annuler
              </button>
              <button
                onClick={saveClient}
                className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Save className="w-5 h-5" />
                <span>{editingClient ? 'Modifier' : 'Créer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManager;